import { ClockIcon, InstagramIcon, LocationIcon, LogoIcon, MailIcon } from '@/assets/icons'
import { formatHoursList } from '@/lib/working-hours'
import { Link } from 'react-router-dom'
import type { PublicStore } from '@/services/store-service'

type FooterProps = {
	store?: Pick<
		PublicStore,
		| 'name'
		| 'tagline'
		| 'address'
		| 'city'
		| 'zip'
		| 'email'
		| 'instagram'
		| 'working_hours'
		| 'logo_url'
	> | null
}

export default function Footer({ store }: FooterProps) {
	// Marketplace home has no store — use platform branding, not a single franchise address
	const isMarketplace = !store
	const brandName = store?.name || 'minmini'
	const tagline =
		store?.tagline ||
		(isMarketplace
			? 'Finde einen Store in deiner Nähe und miete ein Regal.'
			: '(Ver)kaufe nachhaltig, schaffe Platz und verdiene dabei.')
	const addressHtml = store
		? [store.address, [store.zip, store.city].filter(Boolean).join(' ')].filter(Boolean)
		: null
	const email = store?.email || 'support@minmini.de'
	const instagram = store?.instagram?.replace(/^@/, '') || null
	const hours = store ? formatHoursList(store.working_hours) : null

	return (
		<div className="grid grid-cols-1 sm:grid-cols-2 max-lg:gap-6 lg:grid-cols-5 justify-between items-start pt-8 lg:pt-10 pb-8 px-4 sm:px-5 gap-8">
			<div className="max-lg:hidden flex items-center gap-2">
				{store?.logo_url ? (
					<img src={store.logo_url} alt="" className="size-12 2xl:size-16 rounded-xl object-cover" />
				) : (
					<LogoIcon className="size-12 2xl:size-16 shrink-0" />
				)}
				<div className="flex flex-col gap-1">
					<h2 className="text-secondary text-base 2xl:text-xl font-extrabold">{brandName}</h2>
					<p className="text-muted-foreground max-2xl:text-sm font-medium lg:w-10/12">{tagline}</p>
				</div>
			</div>
			<div className="flex flex-col gap-4 lg:gap-6">
				<h2 className="text-secondary lg:text-base 2xl:text-xl font-extrabold">
					Wichtige Informationen
				</h2>
				<Link
					to="/kontakt"
					className="text-muted-foreground text-xs lg:text-sm 2xl:text-base font-medium">
					Impressum
				</Link>
				<Link
					to="/letter"
					className="text-muted-foreground text-xs lg:text-sm 2xl:text-base font-medium">
					AGB
				</Link>
				<Link
					to="/widerruf"
					className="text-muted-foreground text-xs lg:text-sm 2xl:text-base font-medium">
					Widerruf
				</Link>
				<Link
					to="/datenschutz"
					className="text-muted-foreground text-xs lg:text-sm 2xl:text-base font-medium">
					Datenschutz
				</Link>
				<Link
					to="/faqs"
					className="text-muted-foreground text-xs lg:text-sm 2xl:text-base font-medium">
					FAQ
				</Link>
			</div>
			<div className="flex flex-col gap-4 lg:gap-6">
				<h2 className="text-secondary lg:text-xl invisible font-extrabold">Schnellzugriff</h2>
				<Link to="/" className="text-muted-foreground text-xs lg:text-sm 2xl:text-base font-medium">
					Startseite
				</Link>
				<Link
					to="/"
					className="text-muted-foreground text-xs lg:text-sm 2xl:text-base font-medium">
					Regal Mieten
				</Link>
				<Link
					to="https://www.minmini-shop.de/"
					target="__blank"
					className="text-muted-foreground text-xs lg:text-sm 2xl:text-base font-medium">
					minmini Shop
				</Link>
				<Link
					to="/kontakt"
					className="text-muted-foreground text-xs lg:text-sm 2xl:text-base font-medium">
					Kontakt
				</Link>
			</div>
			<div className="flex sm:max-lg:col-span-2 flex-col gap-5 lg:gap-8">
				{addressHtml?.length ? (
					<div className="flex items-center gap-3">
						<LocationIcon className="size-8 2xl:size-10 shrink-0" />
						<p className="text-secondary font-extrabold text-xs 2xl:text-sm">
							{addressHtml[0]}
							{addressHtml[1] ? (
								<>
									<br />
									{addressHtml[1]}
								</>
							) : null}
						</p>
					</div>
				) : (
					<div className="flex items-center gap-3">
						<LocationIcon className="size-8 2xl:size-10 shrink-0" />
						<p className="text-secondary font-extrabold text-xs 2xl:text-sm">
							Stores in deiner Nähe
							<br />
							über die Startseite finden
						</p>
					</div>
				)}
				<div className="flex items-center gap-3">
					<MailIcon className="size-8 2xl:size-10 shrink-0" />
					<div className="flex flex-col gap-1 text-xs 2xl:text-sm">
						{store?.email ? (
							<p className="text-secondary font-extrabold">{email}</p>
						) : (
							<>
								<p className="flex flex-wrap gap-1.5">
									<span className="text-secondary">Allgemeine Anfragen:</span>
									<span className="text-secondary font-extrabold">support@minmini.de</span>
								</p>
								<p className="flex flex-wrap gap-1.5">
									<span className="text-secondary">Geschäftliche Anfragen:</span>
									<span className="text-secondary font-extrabold">info@minmini.de</span>
								</p>
							</>
						)}
					</div>
				</div>
				{hours ? (
					<div className="flex items-start gap-3">
						<ClockIcon className="size-8 2xl:size-10 shrink-0 mt-0.5" />
						<div className="flex flex-col gap-0.5 text-secondary font-extrabold text-xs 2xl:text-sm">
							{hours
								.filter(h => h.text !== 'Geschlossen')
								.slice(0, 4)
								.map(h => (
									<div key={h.day} className="flex gap-2">
										<span className="w-[4ch] shrink-0">{h.day}</span>
										<span>{h.text}</span>
									</div>
								))}
						</div>
					</div>
				) : null}
				{instagram ? (
					<Link
						to={`https://www.instagram.com/${instagram}`}
						target="__blank"
						className="flex lg:hidden items-center gap-3">
						<InstagramIcon className="size-8 2xl:size-10 shrink-0" />
						<p className="text-secondary font-extrabold text-xs 2xl:text-sm">
							Nichts mehr verpassen - <br /> folge uns auf Instagram
						</p>
					</Link>
				) : null}
			</div>
			{instagram ? (
				<Link
					to={`https://www.instagram.com/${instagram}`}
					target="__blank"
					className="flex max-lg:hidden items-center gap-3">
					<InstagramIcon className="size-8 2xl:size-10 shrink-0" />
					<p className="text-secondary font-extrabold text-xs 2xl:text-sm">
						Nichts mehr verpassen - <br /> folge uns auf Instagram
					</p>
				</Link>
			) : (
				<div className="max-lg:hidden" />
			)}
		</div>
	)
}
